import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { BehaviorSubject, Subject } from 'rxjs';
import { pluck } from 'rxjs/operators';

import { AgendaItemModel } from '../../../models/agenda-item.model';
import { BasicDataModel } from '../../../models/basic-data.model';
import { VoteOptionEnum } from '../../../models/enums/vote-option.enum';
import { GeneralMeetingModel } from '../../../models/general-meeting.model';
import { SimpleItemModel } from '../../../models/simple-item.model';
import { DictionaryType } from '../../../models/types/dictionary.type';
import { VotingInstructionsModel } from '../../../models/voting-instructions.model';
import { BasicDataService } from '../../../services/basic-data.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { AppSessionStorage } from '../../../services/storage/app.session-storage';

@Component({
    selector: 'sl-agenda-items',
    templateUrl: './agenda-items.component.html',
    styleUrls: ['./agenda-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgendaItemsComponent implements OnInit, AfterViewInit {
    @ViewChild('stepper', { static: true }) stepper: MatStepper;
    @ViewChildren('agendaItemRow', { read: ElementRef }) agendaItems: QueryList<ElementRef>;

    VoteOptionEnum = VoteOptionEnum;
    formGroup: UntypedFormGroup;
    items: AgendaItemModel[] = [];
    selectedVotes: DictionaryType<VoteOptionEnum> = {};
    votingInstructions: SimpleItemModel[] = [];
    isLoading = true;
    isSaving = false;
    showAdditionalProposalsStep = false;
    isDirty = false;
    hasPresetInstructions = false;
    hasNext: Subject<boolean> = new BehaviorSubject(false);
    currentGM: GeneralMeetingModel;
    instructionsText = '';

    get hasPrevious(): boolean {
        return this.stepper.selectedIndex > 0;
    }

    get selectedAdditionalProposals(): string {
        // eslint-disable-next-line eqeqeq
        const idx = this.votingInstructions.findIndex(vi => vi.id == this.formGroup.value.additionalProposals);
        return idx >= 0 ? this.votingInstructions[idx].label : '';
    }

    get selectedNewItems(): string {
        // eslint-disable-next-line eqeqeq
        const idx = this.votingInstructions.findIndex(vi => vi.id == this.formGroup.value.newItems);
        return idx >= 0 ? this.votingInstructions[idx].label : '';
    }

    get allVotesSelected(): boolean {
        return this.items.filter(i => !this.hasChildren(i)).length === Object.keys(this.selectedVotes).length;
    }

    private readonly data = inject<VotingInstructionsModel>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly basicDataService = inject(BasicDataService);
    private readonly appSessionStorage = inject(AppSessionStorage); // TODO: Part of the temporary hack - to be removed.

    ngOnInit(): void {
        this.formGroup = this.formBuilder.group({
            additionalProposals: new FormControl(undefined, Validators.required),
            newItems: new FormControl(undefined, Validators.required)
        });

        this.setupVotingInstructions();
        this.generalMeetingsResource.getCurrentAgendaItems().subscribe(r => {
            this.items = r.value;

            if (!this.data.isEditing) {
                this.isLoading = false;
            }
        });
        this.basicDataService.get().subscribe(r => {
            this.setupPresetInstructions(r);
            this.generalMeetingsResource.getCurrent().subscribe(c => {
                this.currentGM = c.value;

                if (this.showAdditionalProposalsStep && !this.currentGM.splitInstructions) {
                    this.formGroup = this.formBuilder.group({
                        additionalProposals: new FormControl(undefined, Validators.required)
                    });
                }

                if (this.data.isEditing) {
                    this.setupEditData();
                }
            });
        });
        this.stepper.selectionChange.pipe(pluck('selectedIndex')).subscribe((i: number) => this.hasNext.next(this.checkHasNext(i)));
    }

    ngAfterViewInit(): void {
        // Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
        setTimeout(() => {
            // fixes ExpressionChangedAfterItHasBeenCheckedError see: https://angular.io/errors/NG0100
            this.hasNext.next(this.checkHasNext());
        });
    }

    save(): void {
        if (this.formGroup.valid && this.allVotesSelected) {
            this.isSaving = true;
            this.generalMeetingsResource
                .registerInstructions(this.selectedVotes, this.formGroup.value.additionalProposals, this.formGroup.value.newItems)
                .subscribe(() => this.dialogRef.close());
        }
    }

    /**
     ***************************************************
     * TODO: TEMPORARY HACK - WILL BE REMOVED IN 4 WEEKS
     ***************************************************
     */
    get isMS(): boolean {
        return this.appSessionStorage.getCurrentContext() === 'multiplesklerose';
    }

    checkHasNext(selectedIndex?: number): boolean {
        if (!selectedIndex) {
            selectedIndex = this.stepper.selectedIndex;
        }

        return !this.stepper.steps || selectedIndex + 1 < this.stepper.steps.length;
    }

    notSelectedItems(): AgendaItemModel[] {
        return this.items.filter(i => !this.hasChildren(i) && !Object.keys(this.selectedVotes).includes(i.gmAgendaItemNumber.toString()));
    }

    nextStep(): void {
        this.isDirty = true;

        // If the second step is the Additional proposals and ammendments and the form is invalid.
        if (this.stepper.selectedIndex === 1 && this.stepper.steps.length > 2 && this.formGroup.invalid) {
            this.formGroup.controls['additionalProposals'].updateValueAndValidity();

            if (!!this.formGroup.controls['newItems']) {
                this.formGroup.controls['newItems'].updateValueAndValidity();
            }
        }

        if (this.allVotesSelected) {
            this.stepper.next();
        } else {
            this.notSelectedItems().forEach(n => (n.hasError = true));

            const first = this.notSelectedItems()[0];
            const indexOfFirst = this.items.indexOf(first);
            const elementRef = this.agendaItems.find((item, index) => index === indexOfFirst);

            elementRef?.nativeElement?.scrollIntoView();
        }
    }

    changeVote(id: number, option: VoteOptionEnum): void {
        this.selectedVotes[id] = option;

        const item = this.items.find(i => i.gmAgendaItemNumber === id);
        if (this.isDirty && !!item) {
            item.hasError = false;
        }
    }

    setAllTo(option: VoteOptionEnum): void {
        this.selectedVotes = {};

        this.items.forEach(i => {
            if (!this.hasChildren(i)) {
                this.selectedVotes[i.gmAgendaItemNumber] = option;
            }
        });
        this.changeDetectorRef.detectChanges();
    }

    acceptMotionBoD(): void {
        this.selectedVotes = {};

        this.items.forEach(i => {
            if (!this.hasChildren(i)) {
                this.selectedVotes[i.gmAgendaItemNumber] = i.boardOfDirectorsForOptionsNumber;
                const item = this.items.find(it => it.gmAgendaItemNumber === i.gmAgendaItemNumber);
                if (!!item) {
                    item.hasError = false;
                }
            }
        });
        this.changeDetectorRef.detectChanges();
    }

    isChecked(id: string, option: VoteOptionEnum): boolean {
        if (this.selectedVotes.hasOwnProperty(id)) {
            return this.selectedVotes[id] === option;
        }

        return false;
    }

    hasChildren(item: AgendaItemModel): boolean {
        return this.items.findIndex(i => i.parentGmAgendaItemNumber === item.gmAgendaItemNumber) >= 0;
    }

    private setupVotingInstructions(): void {
        Object.keys(this.data.generalMeeting.votingInstructions).forEach(k =>
            this.votingInstructions.push(
                new SimpleItemModel({
                    id: k,
                    label: this.data.generalMeeting.votingInstructions[k] ?? ''
                })
            )
        );
    }

    private setupEditData(): void {
        this.formGroup.patchValue({
            additionalProposals: String(this.data.shareholderData.instructionAgendaItemChange)
        });

        if (!!this.formGroup.controls['newItems']) {
            this.formGroup.patchValue({
                newItems: this.data.shareholderData.instructionNewAgendaItem
            });
        }

        this.generalMeetingsResource.getCurrentAgendaItemsForShareholder().subscribe(r => {
            r.value
                .filter(i => i.vote > 0)
                .forEach(item => {
                    if (!this.hasChildren(item)) {
                        this.selectedVotes[item.gmAgendaItemNumber] = <VoteOptionEnum>item.vote;
                    }
                });

            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
        });
    }

    private setupPresetInstructions(data: BasicDataModel): void {
        this.hasPresetInstructions =
            data.netvoteShowAbstentionInstruction
            || data.netvoteShowBodAgreeInstruction
            || data.netvoteShowBodDisagreeInstruction
            || data.netvoteShowNoInstruction
            || data.netvoteShowYesInstruction;
        this.showAdditionalProposalsStep = this.hasPresetInstructions;
        this.instructionsText = data.netvoteInstructionText;

        if (!this.showAdditionalProposalsStep) {
            this.formGroup = this.formBuilder.group({});
        }
    }
}
